import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { Close } from "@mui/icons-material";
import { MoonLoader } from "react-spinners";
import { setAlgoPreview } from "../../../../../store/actions/algo";
import { useDispatch, useSelector } from "react-redux";
import CancelTargetListModal from "./cancel-target-list";

const progressStep = [
  {
    title: "The AI is processing your request",
    description:
      "Our AI engine is initializing and preparing to analyze your data. This should take just a moment.",
  },
  {
    title: "Analyzing your company profile",
    description:
      "We're examining your company's industry, size, offerings, and growth trajectory to identify the best potential matches.",
  },
  {
    title: "Generating a list of potential buyers",
    description:
      "Based on your profile, we're searching our database for companies that might be interested in acquiring businesses like yours.",
  },
  {
    title: "Finalizing your target list",
    description:
      "We're refining the results to ensure you receive only the most relevant and promising potential buyers.",
  },
];

const AlgoElapsedTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = Math.floor((now - new Date(startTime).getTime()) / 1000);
      setElapsed(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const formatTime = (seconds) => {
    if (!seconds) return "--:--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [
      h > 0 ? String(h).padStart(2, "0") : null,
      String(m).padStart(2, "0"),
      String(s).padStart(2, "0"),
    ]
      .filter(Boolean)
      .join(":");
  };

  return <span>{formatTime(elapsed)}</span>;
};

const TargetListProgresstModal = () => {
  // STATES
  const [cancelPreview, setCancelPreview] = useState(false);

  // REDUX DATA
  const { companies } = useSelector((state) => state.algoReducer);
  const [selectedId, selectedCompany] =
    Object.entries(companies).find(([_, c]) => c.preview && c.running) || [];

  // CONST VALS
  const dispatch = useDispatch();
  const preview = selectedCompany?.preview || false;
  const currentStep = selectedCompany?.progress || 0;
  const startTime = selectedCompany?.calledAt || null;
  const isCreator = selectedCompany?.creator || false;

  // HANDLERS
  const cancelPreviewHandler = () => {
    setCancelPreview(!cancelPreview);
  };

  const previewHandler = () => {
    dispatch(setAlgoPreview({ companyId: selectedId, preview: false }));
  };

  return (
    <>
      <Modal
        open={preview && !cancelPreview}
        footer={null}
        width={610}
        centered
        className="target-ai-list-modal"
        onCancel={previewHandler}
        closable={false}
      >
        <button className="close-btn" onClick={previewHandler}>
          <Close />
        </button>
        <h2>Great! We're building your results</h2>
        <h5>Feel free to come back after the estimated time</h5>

        <div className="estimate-box">
          <p>
            Estimated time: <b>5–8 minutes</b>
          </p>
          <p>
            Elapsed time:{" "}
            <b>
              <AlgoElapsedTimer startTime={startTime} />
            </b>
          </p>
        </div>

        {progressStep.map((step, index) => (
          <div className="progress-card" key={index}>
            <div className="thumb">
              {index + 1 === currentStep ? (
                <MoonLoader size={20} speedMultiplier={0.5} />
              ) : index + 1 < currentStep ? (
                <div className="progress-icon">
                  <CheckCircleFilled className="check-icon" />
                </div>
              ) : (
                <div className="progress-placeholder" />
              )}
            </div>
            <div className="content">
              <h4>{step.title}</h4>
              <p>{step.description}</p>
            </div>
          </div>
        ))}
        {isCreator && (
          <div className="bottom-wrapper">
            <button className="leads-btn outlined" onClick={cancelPreviewHandler}>
              Cancel Request
            </button>
          </div>
        )}
      </Modal>
      <CancelTargetListModal
        preview={cancelPreview}
        algoId={selectedCompany?.algoId}
        previewHandler={cancelPreviewHandler}
        selectedCompany={selectedId}
      />
    </>
  );
};

export default TargetListProgresstModal;
